import { distributeSlots } from "../lib/distributeSlots";
import type { StatusSegment } from "./StatusBreakdownChart";

// squarify lays the smallest tile out as a thin strip whose short side is
// area ÷ long side, so a tiny space (or the rolled-up "N more" bucket) collapses
// to a hairline. Flooring each tile's area — re-sharing the shortfall from the
// larger tiles — targets a readable short side (~30px in a typical ~150px column),
// trading exact proportionality on the long tail for legibility, the same way
// the donut's distributeSlots does. The precise value stays in the tooltip.
export const MIN_TILE_AREA = 4500;

type Item = { seg: StatusSegment; area: number };
export type TreemapRect = {
  seg: StatusSegment;
  x: number;
  y: number;
  w: number;
  h: number;
};

function worstRatio(areas: number[], side: number): number {
  const sum = areas.reduce((a, b) => a + b, 0);
  const max = Math.max(...areas);
  const min = Math.min(...areas);
  const s2 = sum * sum;
  const side2 = side * side;
  return Math.max((side2 * max) / s2, s2 / (side2 * min));
}

// Squarified treemap (Bruls, Huizing & van Wijk): greedily packs rows that keep
// tiles close to square, recursing into the leftover rectangle.
export function squarify(
  items: Item[],
  width: number,
  height: number
): TreemapRect[] {
  const result: TreemapRect[] = [];
  let rest = [...items];
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;

  while (rest.length > 0) {
    const side = Math.min(w, h);
    const row: Item[] = [];
    const rowAreas: number[] = [];
    while (rest.length > 0) {
      const next = rest[0]!;
      const candidate = [...rowAreas, next.area];
      if (
        row.length === 0 ||
        worstRatio(rowAreas, side) >= worstRatio(candidate, side)
      ) {
        row.push(next);
        rowAreas.push(next.area);
        rest = rest.slice(1);
      } else {
        break;
      }
    }

    const rowArea = rowAreas.reduce((a, b) => a + b, 0);
    if (w >= h) {
      const stripW = rowArea / h;
      let cy = y;
      for (const cell of row) {
        const cellH = cell.area / stripW;
        result.push({ seg: cell.seg, x, y: cy, w: stripW, h: cellH });
        cy += cellH;
      }
      x += stripW;
      w -= stripW;
    } else {
      const stripH = rowArea / w;
      let cx = x;
      for (const cell of row) {
        const cellW = cell.area / stripH;
        result.push({ seg: cell.seg, x: cx, y, w: cellW, h: stripH });
        cx += cellW;
      }
      y += stripH;
      h -= stripH;
    }
  }
  return result;
}

// Ranks segments by count, floors each tile to MIN_TILE_AREA (taking the
// shortfall from the larger tiles), then squarifies. Callers pass only
// positive-count segments.
export function layoutTreemap(
  segments: readonly StatusSegment[],
  width: number,
  height: number
): TreemapRect[] {
  const sorted = [...segments].sort((a, b) => b.count - a.count);
  // Clamp the floor to the container: capping it at half a tile's fair share
  // keeps distributeSlots out of its equal-split branch (which drops all
  // proportionality) when many spaces share a small box, so tiny tiles still
  // thicken as much as the box allows without swallowing the whole layout.
  const minArea = Math.min(
    MIN_TILE_AREA,
    (width * height) / (2 * sorted.length)
  );
  const areas = distributeSlots(
    sorted.map((seg) => seg.count),
    width * height,
    minArea
  );
  const items = sorted.map((seg, i) => ({ seg, area: areas[i]! }));
  return squarify(items, width, height);
}
